<script setup lang="ts">
import { i18n } from '../../../localization'
import AccountPanel, { RouterItem } from '../AccountPanel.vue'
import {
    LegalAccountNavigationStore,
    LegalAccountPage,
    navigationItemsLegal,
    useLegalAccountNavigationState,
} from '../../../utilities/account/useAccountNavigationLegal'
import AccountLegalDashboard from './AccountLegalDashboard.vue'
import { useLegalAccountDataState } from '../../../utilities/account/useAccountDataLegal'
import AccountPanelLegalCompanyData from './pages/AccountPanelLegalCompanyData.vue'
import { useLegalAccountFormState } from '../../../utilities/account/useAccountFormLegal'
import AccountPanelLegalAddressData from './pages/AccountPanelLegalAddressData.vue'
import AccountPanelLegalBankData from './pages/AccountPanelLegalBankData.vue'
import AccountPanelLegalTaxData from './pages/AccountPanelLegalTaxData.vue'
import AccountPanelLegalVerification from './pages/AccountPanelLegalVerification.vue'
import AccountPanelLegalMissingDataAlert from './AccountPanelLegalMissingDataAlert.vue'
import AccountPanelLegalCryptoWallet from './pages/AccountPanelLegalCryptoWallet.vue'
import { computed, onMounted, ref, watch, watchEffect } from 'vue'
import AccountPanelLegalMissingVerificationAlert from './AccountPanelLegalMissingVerificationAlert.vue'
import KycVerificationStatus from '../common/verification/KycVerificationStatus.vue'
import AccountPanelLegalManagerArea from './pages/AccountPanelLegalManagerArea.vue'
import AccountPanelLegalBeneficialOwners from './pages/AccountPanelLegalBeneficialOwners.vue'
import { getIsIbanFromBankAccount, getPlatformSettings } from '../../../utilities/usePlatformSettings'
import AccountPanelLegalSignatures from './pages/AccountPanelLegalSignatures.vue'
import AccountPanelLegalComplianceProfile from './pages/AccountPanelLegalComplianceProfile.vue'
import AccountPanelLegalMissingSignaturesAlert from './AccountPanelLegalMissingSignaturesAlert.vue'
import Alert from '../../base/Alert.vue'
import { ExclamationCircleIcon, UserCircleIcon } from '@heroicons/vue/solid'
import { isEsacLegalComplianceProfileLocked } from './complianceProfile/esacComplianceProfile'
import MembershipManagementPanel from '../membershipManagement/MembershipManagementPanel.vue'
import AccountPanelLegalPendingInvestmentProcessAlert from './AccountPanelLegalPendingInvestmentProcessAlert.vue'

const navigationStore: LegalAccountNavigationStore = useLegalAccountNavigationState() as LegalAccountNavigationStore
const store = useLegalAccountDataState()
const accountFormStore = useLegalAccountFormState()
const condaUseV2FrontendPortfolioDashboard = ref<number>(0)

watchEffect(() => {
    condaUseV2FrontendPortfolioDashboard.value = window.condaUseV2FrontendPortfolioDashboard
})

const router: { [key in LegalAccountPage]: RouterItem } = {
    'Dashboard': {
        title: i18n.global.t('account_settings_legal.dashboard'),
        component: AccountLegalDashboard,
        hideFooter: computed(() => true),
        customPanel: computed(() => !!condaUseV2FrontendPortfolioDashboard.value),
    },
    'Company Data': {
        title: i18n.global.t('account_settings_legal.company_data'),
        component: AccountPanelLegalCompanyData,
    },
    'Address Data': {
        title: i18n.global.t('account_settings_legal.address_data'),
        component: AccountPanelLegalAddressData,
    },
    'Bank Data': {
        title: i18n.global.t('account_settings_legal.bank_data'),
        component: AccountPanelLegalBankData,
        hideFooter: computed(() => getIsIbanFromBankAccount()),
    },
    'Tax Data': {
        title: i18n.global.t('account_settings_legal.tax_data'),
        component: AccountPanelLegalTaxData,
    },
    'Verification': {
        title: i18n.global.t('account_settings_legal.verification'),
        component: AccountPanelLegalVerification,
        subtitle: i18n.global.t('account_settings_legal.verification_kyc_documents_subtitle'),
        hideFooter: computed(() => true),
    },
    'Crypto Wallet': {
        title: i18n.global.t('account_settings.crypto_wallet_headline'),
        component: AccountPanelLegalCryptoWallet,
        hideFooter: computed(() => true),
    },
    'Manager': {
        title: i18n.global.t('account_settings_legal.manager_area'),
        subtitle: i18n.global.t('account_settings_legal.manager_area_subtitle'),
        component: AccountPanelLegalManagerArea,
        hideFooter: computed(() => true),
    },
    'Beneficial Owners': {
        title: i18n.global.t('account_settings_legal.beneficial_owners'),
        subtitle: i18n.global.t('account_settings_legal.beneficial_owners_subtitle'),
        component: AccountPanelLegalBeneficialOwners,
        hideFooter: computed(() => true),
    },
    'Compliance Profile': {
        title: i18n.global.t('account_settings.compliance_profile'),
        component: AccountPanelLegalComplianceProfile,
        hideFooter: computed(() => true),
        hideHeader: computed(() => useLegalAccountFormState().currentForm !== null),
    },
    'Signatures': {
        title: i18n.global.t('account_settings.signatures_headline'),
        subtitle: i18n.global.t('account_settings.signatures_subtitle'),
        component: AccountPanelLegalSignatures,
        hideFooter: computed(() => true),
    },
    'Membership Management': {
        title: i18n.global.t('account_settings.membership_management'),
        component: MembershipManagementPanel,
        hideFooter: computed(() => true),
    },
}

const showDataMissingAlert = ref(false)
const showVerificationMissingAlert = ref(false)

onMounted(() => {
    showDataMissingAlert.value = !store.hasAllRequiredProfileAttributesForInvestment
    showVerificationMissingAlert.value = !store.legalProfile.kyc_legal
})

watch(
    () => store.saving,
    () => {
        if (!store.hasAllRequiredProfileAttributesForInvestment) {
            showDataMissingAlert.value = true
        }
    }
)

const showMissingSignaturesAlert = computed(() => {
    return (
        !!store.legalProfile.signatures.find((signature) => signature.signed_at === null) ||
        !!store.legalProfile.beneficial_owners.find(
            (beneficialOwner) => !!beneficialOwner.signatures.find((signature) => signature.signed_at === null)
        )
    )
})
</script>

<template>
    <account-panel
        :store="store"
        :router="router"
        :navigation-store="navigationStore"
        :account-form-store="accountFormStore"
        :navigation-items="navigationItemsLegal">
        <template #alerts>
            <div class="mb-4 space-y-4">
                <AccountPanelLegalPendingInvestmentProcessAlert></AccountPanelLegalPendingInvestmentProcessAlert>
                <AccountPanelLegalMissingDataAlert v-if="showDataMissingAlert" />
                <AccountPanelLegalMissingVerificationAlert
                    v-if="showVerificationMissingAlert"></AccountPanelLegalMissingVerificationAlert>
                <AccountPanelLegalMissingSignaturesAlert
                    v-if="showMissingSignaturesAlert"></AccountPanelLegalMissingSignaturesAlert>
                <Alert
                    v-if="getPlatformSettings().compliance_profile === 'esac' && isEsacLegalComplianceProfileLocked()"
                    :title-text="$t('account.esac_locked_account_info')">
                    <template #icon>
                        <UserCircleIcon class="h-12 w-12 text-gray-300"></UserCircleIcon>
                        <ExclamationCircleIcon
                            class="absolute bottom-0 right-0 h-6 w-6 text-yellow-400"></ExclamationCircleIcon>
                    </template>
                </Alert>
                <div
                    v-if="showDataMissingAlert || showVerificationMissingAlert || showMissingSignaturesAlert"
                    class="border-b border-gray-300 pt-2"></div>
            </div>
        </template>
        <template #header-right>
            <KycVerificationStatus v-if="navigationStore.page === 'Verification'" :is-verified="store.isVerified" />
            <KycVerificationStatus
                v-if="navigationStore.page === 'Bank Data' && getIsIbanFromBankAccount()"
                :is-verified="store.legalProfile.kyc_bank" />
        </template>
    </account-panel>
</template>
